/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { ReactNode } from 'react';

import isEqual from 'lodash/isEqual';
import some from 'lodash/some';

import { DropdownProps, AutocompleteOverrides, DropdownSize } from './types';

const legacySizeMap = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
} as const;

/**
 * Maps Dropdown sizes to the legacy size names still used by Input, List
 * and Checkbox. Remove once those components migrate to `Sizes`.
 */
export const toLegacySize = (size: DropdownSize | undefined) =>
  size ? legacySizeMap[size] : undefined;

const makeGetters = <
  OptionItem,
  OptionValue,
  SelectionType extends 'single' | 'multiple' | undefined
>(
  dropdownProps: DropdownProps<OptionItem, OptionValue, SelectionType>
) => {
  const {
    optionLabel,
    optionDisabled,
    isOptionEqualToValue,
    groupBy,
    optionKey,
    optionValue,
  } = dropdownProps;

  let getOptionLabel: (option: OptionItem) => string;
  if (typeof optionLabel === 'string') {
    getOptionLabel = (option) => (option[optionLabel] as string) || '';
  } else if (typeof optionLabel === 'function') {
    getOptionLabel = (option) => optionLabel(option) || '';
  } else {
    getOptionLabel = (option) => String(option ?? '');
  }

  let getOptionIcon: ((option: OptionItem) => ReactNode) | undefined;
  if ('optionIcon' in dropdownProps) {
    const { optionIcon } = dropdownProps;
    if (typeof optionIcon === 'function') {
      getOptionIcon = optionIcon;
    } else if (typeof optionIcon === 'string') {
      getOptionIcon = (option) => option[optionIcon] as ReactNode;
    }
  }

  let groupByFn: (option: OptionItem) => string;
  if (typeof groupBy === 'string') {
    groupByFn = (option) => option[groupBy] as string;
  } else {
    groupByFn = groupBy as (option: OptionItem) => string;
  }

  let getOptionKey: (option: OptionItem) => string;
  if (typeof optionKey === 'function') {
    getOptionKey = optionKey;
  } else if (typeof optionKey === 'string') {
    getOptionKey = (option) => (option[optionKey] as string) || '';
  } else {
    getOptionKey = getOptionLabel;
  }

  let getOptionSelectedValue: (option: OptionItem) => ReactNode;
  const isSingleSelection = dropdownProps.multiple !== true;
  if (isSingleSelection) {
    const { renderValue } = dropdownProps;
    if (typeof renderValue === 'function') {
      getOptionSelectedValue = renderValue;
    } else if (typeof renderValue === 'string') {
      getOptionSelectedValue = (option) =>
        (option[renderValue] as string) || '';
    } else {
      getOptionSelectedValue = getOptionLabel;
    }
  } else {
    getOptionSelectedValue = getOptionLabel;
  }

  let getOptionValue: (option: OptionItem) => OptionValue;
  if (typeof optionValue === 'function') {
    getOptionValue = optionValue;
  } else if (typeof optionValue === 'string') {
    getOptionValue = (option) => option?.[optionValue] as OptionValue;
  } else {
    getOptionValue = (option) => option as unknown as OptionValue;
  }

  return {
    getOptionIcon,
    getOptionLabel,
    groupBy: groupByFn,
    getOptionKey,
    getOptionSelectedValue,
    getOptionValue,
    getOptionDisabled: optionDisabled || ((_option: OptionItem) => false),
    isOptionEqualToValue:
      isOptionEqualToValue ??
      (optionValue
        ? (option, value) => {
            const opt =
              typeof option === 'object' ? getOptionValue(option) : option;
            const val =
              typeof value === 'object'
                ? getOptionValue(value as OptionItem)
                : value;
            return opt === val;
          }
        : (option, value) => isEqual(option, value)),
  };
};

type NormalizedProps<
  OptionItem,
  OptionValue,
  SelectionType extends 'single' | 'multiple' | undefined
> = {
  getters: ReturnType<
    typeof makeGetters<OptionItem, OptionValue, SelectionType>
  >;
  autocompleteProps?: AutocompleteOverrides<
    OptionItem,
    OptionValue,
    SelectionType
  >;
};

export const normalizeProps = <
  OptionItem,
  OptionValue,
  SelectionType extends 'single' | 'multiple' | undefined
>(
  props: DropdownProps<OptionItem, OptionValue, SelectionType>
): NormalizedProps<OptionItem, OptionValue, SelectionType> => {
  const { options, multiple, open, onChange, onOpen, onClose } = props;
  const getters = makeGetters(props);
  const autocompleteProps = {
    options: options || [],
    multiple,
    open,
    onChange: multiple
      ? (option: OptionItem[]) => {
          onChange?.(
            option,
            Array.isArray(options)
              ? options.map((option) => getters.getOptionValue(option))
              : []
          );
        }
      : (option: OptionItem) =>
          onChange?.(option, getters.getOptionValue(option)),
    value: props.value,
    applySortingWhileSearching: props.applySortingWhileSearching ?? true,
    searchFilter: props.searchFilter,
    resetInputOnClose: true,
    onOpen,
    onClose,
  };
  return { getters, autocompleteProps: autocompleteProps as any };
};

export const makeIsOptionSelected = <OptionItem, OptionValue>(
  multiple: boolean,
  value: OptionValue | OptionValue[] | null | undefined,
  isOptionEqualToValue?:
    | ((option: OptionItem, value: OptionValue) => boolean)
    | ((option: OptionItem, value: OptionValue[]) => boolean)
): ((option: OptionItem) => boolean) => {
  if (multiple) {
    if (isOptionEqualToValue) {
      return (option) =>
        (value as OptionItem[]).some((v) =>
          isOptionEqualToValue?.(option, v as any)
        );
    }
    return (option: OptionItem) => some(value as OptionItem[], option as any);
  }
  if (isOptionEqualToValue) {
    return (option: OptionItem) => isOptionEqualToValue(option, value as any);
  }
  return (option: OptionItem) => value === option;
};
